import React, { useEffect, useRef, useState } from "react";
import { css } from "@emotion/css";

import type { EmissionReceipt } from "@sharpana/session-api/types";

/**
 * SessionReceipt — the ledger tick after a priced close.
 *
 * The learner answered something and it counted.  Nothing else on screen
 * says so: the activity draws its own reveal, and the wrap card is
 * minutes away.  This is the small, quiet acknowledgement in between —
 * "banked on the numb model", or, when a rung actually falls, "crossed
 * OPEN on Attention".
 *
 * Three deliberate constraints:
 *
 * - **Never over the activity.**  It lives in the host's chrome band
 *   above the bottom bar.  A toast floating over the viewport would land
 *   on top of whatever the vessel just revealed.
 * - **Words, never digits.**  The display law, all the way out to here:
 *   the server sends the price LABEL, which is already words, and the
 *   points ride only as the label the pricer wrote.
 * - **It leaves.**  Auto-dismissed, because a receipt that persists
 *   becomes chrome, and chrome that says "+2" next to a wrong answer is
 *   worse than nothing.
 */

const DISMISS_AFTER_MS = 4200;

const bandStyle = css`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 0 16px;
  pointer-events: none;
`;

const tickStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 7px 14px;
  margin-bottom: 6px;
  border-radius: 999px;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-subtle);
  color: var(--color-text-secondary);
  font-size: 0.85em;
  line-height: 1.35;
  animation: receipt-in 0.18s ease-out;

  @keyframes receipt-in {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** A crossing is the one thing here worth raising your voice about. */
const crossingStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-muted);
  color: var(--color-text);
  font-weight: 600;
`;

const glyphStyle = css`
  font-size: 0.95em;
  line-height: 1;
  opacity: 0.9;
`;

const textStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface SessionReceiptProps {
  /** The latest priced close, or null when there is nothing to show. */
  receipt: EmissionReceipt | null;
  /** Titles for the worlds, so the tick names one the way the atlas does. */
  planetTitles?: Record<string, string>;
}

/** What the tick says.  A crossing outranks a price; a price outranks a
 *  bare completion, which says nothing at all. */
export function receiptLine(
  receipt: EmissionReceipt,
  planetTitles: Record<string, string> = {},
): { text: string; crossed: boolean } | null {
  const crossing = receipt.crossings?.[0];
  const world = (planet: string) => planetTitles[planet] || planet;
  if (crossing) {
    return { text: `crossed ${crossing.rung} on ${world(crossing.planet)}`, crossed: true };
  }
  if (receipt.label) {
    return { text: `${receipt.label} — ${world(receipt.planet)}`, crossed: false };
  }
  return null;
}

export const SessionReceipt: React.FC<SessionReceiptProps> = ({ receipt, planetTitles }) => {
  const [shown, setShown] = useState<EmissionReceipt | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!receipt) return undefined;
    setShown(receipt);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setShown(null), DISMISS_AFTER_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [receipt]);

  const line = shown ? receiptLine(shown, planetTitles) : null;

  // `role="status"` + polite: a screen reader hears it when it is free,
  // never interrupting the reveal the learner is reading.
  return (
    <div className={bandStyle} role="status" aria-live="polite" data-testid="session-receipt">
      {line && (
        <div
          className={`${tickStyle}${line.crossed ? ` ${crossingStyle}` : ""}`}
          data-testid="session-receipt-tick"
          data-crossed={line.crossed ? "true" : "false"}
        >
          <span className={glyphStyle} aria-hidden="true">
            {line.crossed ? "✦" : "✓"}
          </span>
          <span className={textStyle}>{line.text}</span>
        </div>
      )}
    </div>
  );
};

export default SessionReceipt;
